'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Popover, PopoverContent, PopoverTrigger } from '@workspace/ui';
import { cn, getInitials } from '@workspace/ui/lib';
import type { JwtPayload } from '@workspace/ui/types';
import { jwtDecode } from 'jwt-decode';
import { ChevronsUpDown, LoaderCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getOrganisationsByUserIds, updateCurrentOrganisation } from '@/actions/organisation';
import { useCanShowMultiOrganisationSwitcher } from '@/hooks/use-can-show-multi-organisation-switcher';
import { createJwtUserFromPayload, getActiveOrganisationFromUser } from '@/lib/active-organisation';
import {
  broadcastOrganisationSwitch,
  reloadAfterOrganisationSwitch,
} from '@/lib/organisation-switch-channel';
import { useAuthStore } from '@/stores/authStore';

export function OrganisationSwitcher() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const setAccessToken = useAuthStore((state) => state.setAccessToken);
  const setAuthenticated = useAuthStore((state) => state.setAuthenticated);
  const setUser = useAuthStore((state) => state.setUser);
  const setIsLoading = useAuthStore((state) => state.setIsLoading);
  const setOrganisationSwitching = useAuthStore((state) => state.setOrganisationSwitching);

  const userId = user?.user.id;
  const activeOrganisationId = getActiveOrganisationFromUser(user)?.id;
  const canShowSwitcher = useCanShowMultiOrganisationSwitcher();

  const { data: organisationsResponse, isLoading } = useQuery({
    queryKey: ['organisations-by-user-ids', userId],
    queryFn: () => getOrganisationsByUserIds(userId ? [userId] : []),
    enabled: !!userId && canShowSwitcher,
    staleTime: 10 * 60 * 1000,
  });

  const organisations = organisationsResponse?.data ?? [];
  const activeOrganisation = organisations.find(
    (organisation) => organisation.id === activeOrganisationId
  );

  const switchMutation = useMutation({
    mutationFn: (organisationId: number) =>
      userId
        ? updateCurrentOrganisation({ user_id: userId, organisation_id: organisationId })
        : Promise.reject(new Error('Missing user id')),
    onMutate: () => {
      setOrganisationSwitching(true);
      setIsLoading(true);
    },
    onSuccess: (response, organisationId) => {
      const accessToken = response.data?.auth.access_token;
      if (!accessToken) {
        setOrganisationSwitching(false);
        setIsLoading(false);
        toast.error('Organisation switched, but no access token was returned.');
        return;
      }

      const decodedToken = jwtDecode<JwtPayload>(accessToken);
      setAccessToken(accessToken);
      setAuthenticated(true);
      setUser(createJwtUserFromPayload(decodedToken));
      queryClient.clear();
      broadcastOrganisationSwitch(organisationId);
      reloadAfterOrganisationSwitch(organisationId);
    },
    onError: (error) => {
      setOrganisationSwitching(false);
      setIsLoading(false);
      toast.error(error instanceof Error ? error.message : 'Failed to switch organisation.');
    },
  });

  if (!userId || !canShowSwitcher || organisations.length <= 1) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'bg-grey-100 text-neutral-200 cursor-pointer dark:bg-grey-900 dark:text-neutral-100 flex h-[30px] w-[225px] items-center gap-2 rounded-full px-2 text-sm',
            'hover:bg-grey-200 focus-visible:ring-primary focus-visible:ring-2 focus-visible:outline-none dark:hover:bg-grey-800'
          )}
          disabled={isLoading || switchMutation.isPending}
        >
          <div className="bg-neutral-200 text-neutral-100 flex size-5 shrink-0 items-center justify-center rounded-full text-[10px]">
            <p>
              {getInitials(
                activeOrganisation?.brand_name ?? activeOrganisation?.registered_name ?? ''
              )}
            </p>
          </div>
          <p className="min-w-0 flex-1 truncate text-left">
            {activeOrganisation?.brand_name ??
              activeOrganisation?.registered_name ??
              'Organisation'}
          </p>
          {switchMutation.isPending ? (
            <LoaderCircle className="size-4 shrink-0 animate-spin" />
          ) : (
            <ChevronsUpDown className="size-4 shrink-0" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={12}
        className="bg-grey-0 dark:bg-grey-1000 w-[300px] gap-2 rounded-4xl p-4"
      >
        {organisations.map((organisation) => {
          const isActive = organisation.id === activeOrganisationId;
          return (
            <button
              key={organisation.id}
              type="button"
              className={cn(
                'text-neutral-200 cursor-pointer dark:text-neutral-100 hover:bg-grey-50 dark:hover:bg-grey-900 flex h-12 w-full items-center gap-3 rounded-xl px-3 text-left text-sm',
                isActive && 'bg-grey-50 dark:bg-grey-900'
              )}
              disabled={isActive || switchMutation.isPending}
              onClick={() => switchMutation.mutate(organisation.id)}
            >
              <div className="bg-neutral-200 text-neutral-100 flex size-7 shrink-0 items-center justify-center rounded-full text-xs">
                <p>{getInitials(organisation.brand_name || organisation.registered_name)}</p>
              </div>
              <div className="flex min-w-0 flex-1 flex-col">
                <p className="truncate font-medium">{organisation.brand_name}</p>
                {organisation.registered_name && (
                  <p className="text-grey-600 truncate text-xs">{organisation.registered_name}</p>
                )}
              </div>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
